import { useCallback, useMemo, useState, useEffect } from "react"
import { ChevronDown } from "lucide-react"
import {
  useFetchPendingSubscriptionEventsQuery,
  useFindRateflowLogByIdQuery
} from "../../../../../redux/queries"
import { useFetchPendingSubscriptionByIdQuery } from "../../../../../redux/queries"
import { PendingSubscriptionEvent, RateflowLog } from "../../../../../types"
import {
  DEFAULT_DATETIME_DAY_FORMAT,
  formatDateTimeFromMillis,
  formatRateflowCard
} from "../../../../utils"
import QuotesGroup from "../../../engagements/details/sections/quote-history/quotes-group"
import { QuotesGroupSkeleton } from "../../../skeletons"
import QuoteDetails from "../../../engagements/details/sections/quote-history/quote-details"
import { DateTime } from "luxon"
import { SelectOptions } from "../../../../../types"
import DateFilters from "./date-filters"
import MostRecentQuoteSection from "./most-recent-section"

type Props = {
  subscriptionId: number
}

const RANGE_OPTIONS: SelectOptions = [
  {
    id: "last_five",
    name: "Last five quotes"
  },
  {
    id: "custom",
    name: "Custom dates"
  }
]

const DreamRateQuoteHistory = ({ subscriptionId }: Props) => {
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null)
  const [expandedDates, setExpandedDates] = useState<Set<number>>(new Set())
  const [range, setRange] = useState<"last_five" | "custom">("last_five")
  const [startDate, setStartDate] = useState<Date | null>(new Date())
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [fetchedRateflowLogs, setFetchedRateflowLogs] = useState<
    Record<string, { log: RateflowLog; timestamp: number }>
  >({})

  // Get pending subscription data for loan amount
  const { data: pendingSubscription } = useFetchPendingSubscriptionByIdQuery(
    { id: subscriptionId },
    { skip: !subscriptionId }
  )

  // Fetch all priced events
  const {
    data: pricedEvents = [],
    isFetching: isFetchingEvents,
    isError: eventsError
  } = useFetchPendingSubscriptionEventsQuery({
    pending_id: subscriptionId,
    event_type: "priced"
  })

  // Sort events by created timestamp (descending - most recent first)
  const sortedEvents = useMemo(
    () => [...pricedEvents].sort((a, b) => b.created - a.created),
    [pricedEvents]
  )

  const filteredEvents = useMemo(() => {
    if (!sortedEvents.length) return []

    if (range === "last_five") {
      return sortedEvents.slice(0, 5) // Show only last 5 quotes
    }

    if (range === "custom" && startDate && endDate) {
      const startTimestamp = DateTime.fromJSDate(startDate)
        .startOf("day")
        .toSeconds()
      const endTimestamp = DateTime.fromJSDate(endDate).endOf("day").toSeconds()

      return sortedEvents.filter(
        event =>
          event.created >= startTimestamp && event.created <= endTimestamp
      )
    }

    return sortedEvents
  }, [sortedEvents, range, startDate, endDate])

  // Get the most recent event for auto-loading
  const mostRecentEvent = filteredEvents[0]
  const mostRecentQuoteId = mostRecentEvent?.details?.quote_id

  // Auto-fetch the most recent quote
  const { data: mostRecentRateflowLog, isFetching: isFetchingMostRecent } =
    useFindRateflowLogByIdQuery(mostRecentQuoteId?.toString() ?? "", {
      skip: !mostRecentQuoteId
    })

  useEffect(() => {
    if (mostRecentRateflowLog && mostRecentQuoteId && mostRecentEvent) {
      setFetchedRateflowLogs(prev => ({
        ...prev,
        [mostRecentQuoteId.toString()]: {
          log: mostRecentRateflowLog,
          timestamp: mostRecentEvent.created
        }
      }))
    }
  }, [mostRecentRateflowLog, mostRecentQuoteId, mostRecentEvent])

  // Group events by date (excluding the most recent one)
  const olderEvents = useMemo(() => {
    return filteredEvents.slice(1) // Skip the first (most recent) event
  }, [filteredEvents])

  const loanAmount = pendingSubscription?.quote_info?.loan_amount ?? 0

  // Handle toggling and loading older quotes
  const toggleAndLoadQuote = useCallback(
    (quoteId: number, eventTimestamp: number) => {
      setExpandedDates(prev => {
        const next = new Set(prev)
        if (next.has(eventTimestamp)) next.delete(eventTimestamp)
        else next.add(eventTimestamp)
        return next
      })
    },
    []
  )

  const allCards = useMemo(() => {
    return Object.values(fetchedRateflowLogs).flatMap(({ log, timestamp }) =>
      log.cards.map(card =>
        formatRateflowCard({
          ...card,
          timestamp: timestamp
        })
      )
    )
  }, [fetchedRateflowLogs])

  // Get selected quote data for modal
  const selectedQuote = useMemo(
    () => allCards.find(card => card.hash === selectedQuoteId),
    [allCards, selectedQuoteId]
  )

  // Reset UI state when filters change
  useEffect(() => {
    setExpandedDates(new Set())
    setSelectedQuoteId(null)
  }, [range, startDate, endDate])

  // Close modal if selected card isn't available anymore
  useEffect(() => {
    if (selectedQuoteId && !selectedQuote) setSelectedQuoteId(null)
  }, [selectedQuoteId, selectedQuote])

  if (eventsError) {
    return (
      <div className="mt-3 text-red-500">Failed to load quote history.</div>
    )
  }

  if (isFetchingEvents) {
    return <QuotesGroupSkeleton cardCount={4} />
  }

  if (filteredEvents.length === 0) {
    return (
      <>
        <DateFilters
          range={range}
          setRange={setRange}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          options={RANGE_OPTIONS}
        />
        <div className="mt-3 text-neutral-500">No quotes found.</div>
      </>
    )
  }

  return (
    <div>
      {/* Date filters */}
      <DateFilters
        range={range}
        setRange={setRange}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        options={RANGE_OPTIONS}
      />

      {/* Most recent quote section */}
      <MostRecentQuoteSection
        event={mostRecentEvent}
        quoteId={mostRecentQuoteId}
        isFetching={isFetchingMostRecent}
        cards={mostRecentRateflowLog?.cards}
        loanAmount={loanAmount}
        onSelect={setSelectedQuoteId}
      />

      {/* Older quotes - load on demand */}
      {olderEvents.map(event => {
        const quoteId = event.details?.quote_id
        const isExpanded = expandedDates.has(event.created)

        return (
          <OlderQuoteSection
            key={event.id}
            event={event}
            quoteId={quoteId}
            isExpanded={isExpanded}
            loanAmount={loanAmount}
            onToggle={() =>
              quoteId && toggleAndLoadQuote(quoteId, event.created)
            }
            onSelectQuote={setSelectedQuoteId}
            onLogFetched={(log, id) => {
              setFetchedRateflowLogs(prev => ({
                ...prev,
                [id.toString()]: {
                  log,
                  timestamp: event.created
                }
              }))
            }}
          />
        )
      })}

      {/* Quote details modal */}
      {selectedQuote && (
        <QuoteDetails
          open={!!selectedQuote}
          onClose={() => setSelectedQuoteId(null)}
          data={selectedQuote}
          loanAmount={loanAmount}
        />
      )}
    </div>
  )
}

// Component for older quote sections with lazy loading
const OlderQuoteSection = ({
  event,
  quoteId,
  isExpanded,
  loanAmount,
  onToggle,
  onSelectQuote,
  onLogFetched
}: {
  event: PendingSubscriptionEvent
  quoteId: number | undefined
  isExpanded: boolean
  loanAmount: number
  onToggle: () => void
  onSelectQuote: (id: string) => void
  onLogFetched: (log: RateflowLog, id: number) => void
}) => {
  // Only fetch when expanded
  const { data: rateflowLog, isFetching } = useFindRateflowLogByIdQuery(
    quoteId?.toString() ?? "",
    { skip: !isExpanded || !quoteId }
  )

  useEffect(() => {
    if (rateflowLog && quoteId) {
      onLogFetched(rateflowLog, quoteId)
    }
  }, [rateflowLog, quoteId, onLogFetched])

  const formatDate = (timestamp: number) => {
    return formatDateTimeFromMillis(
      timestamp * 1000,
      DEFAULT_DATETIME_DAY_FORMAT
    )
  }

  return (
    <div className="py-3">
      <div
        onClick={onToggle}
        className="cursor-pointer font-bold text-lg flex items-center -mb-3"
      >
        <span>{formatDate(event.created)}</span>

        <ChevronDown
          className={`ml-auto transition-transform duration-200 ${
            isExpanded ? "rotate-180 " : ""
          }`}
        />
      </div>

      {isExpanded && (
        <div>
          {isFetching ? (
            <QuotesGroupSkeleton cardCount={3} />
          ) : rateflowLog ? (
            <QuotesGroup
              quoteId={quoteId?.toString() ?? ""}
              timestamp={event.created}
              cards={rateflowLog.cards}
              loanAmount={loanAmount}
              onSelect={onSelectQuote}
              hideTitle
            />
          ) : (
            <div className="py-4 text-neutral-500">
              Failed to load quote data.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DreamRateQuoteHistory
